import jsPDF from 'jspdf';

// ─────────────────────────────────────────────
// DESIGN TOKENS
// ─────────────────────────────────────────────
const C = {
  // Brand
  NAVY:        [10,  40,  90],   // Deep navy - header bg
  BLUE:        [24,  90, 180],   // Primary blue - section headers
  BLUE_LIGHT:  [232, 242, 255],  // Very light blue - section header bg
  BLUE_MID:    [180, 210, 250],  // Mid blue - section header accent bar
  TEAL_ACCENT: [0,  160, 160],   // Teal - subsection bullets
  // Neutrals
  WHITE:       [255, 255, 255],
  GRAY_DARK:   [30,  35,  50],   // Text on fields
  GRAY_MID:    [90, 100, 120],   // Labels
  GRAY_LIGHT:  [200, 207, 220],  // Field borders
  GRAY_BG:     [248, 249, 252],  // Field fill
  GRAY_RULE:   [220, 225, 235],  // Separator lines
  FOOTER_BG:   [240, 243, 250],  // Footer strip bg
  FOOTER_TEXT: [110, 120, 145],  // Footer text
  GOLD:        [195, 155, 60],   // Gold accent - logo ring
};

const M = 15;          // page margin
const PAGE_W = 210;    // A4 width mm
const INNER_W = PAGE_W - M * 2;  // 180mm usable
const COL_GAP = 6;
const COL_W   = (INNER_W - COL_GAP) / 2;  // two-column width ~87mm
const FIELD_H = 9;     // input box height
const LABEL_H = 4.5;   // label height above box
const ROW_H   = FIELD_H + LABEL_H + 3; // total row height

// ─────────────────────────────────────────────
// COLOUR HELPERS
// ─────────────────────────────────────────────
const applyFill   = (doc, c) => doc.setFillColor(c[0], c[1], c[2]);
const applyStroke = (doc, c) => doc.setDrawColor(c[0], c[1], c[2]);
const applyText   = (doc, c) => doc.setTextColor(c[0], c[1], c[2]);

// ─────────────────────────────────────────────
// PAGE OVERFLOW GUARD
// ─────────────────────────────────────────────
const ensureSpace = (doc, y, needed = 20) => {
  if (y + needed > 272) {
    doc.addPage();
    return M + 4;
  }
  return y;
};

// ─────────────────────────────────────────────
// HEADER  (page 1 only)
// ─────────────────────────────────────────────
const addHeader = (doc) => {
  const HEADER_H = 52;

  // Navy background
  applyFill(doc, C.NAVY);
  doc.rect(0, 0, PAGE_W, HEADER_H, 'F');

  // Gold accent bar at bottom of header
  applyFill(doc, C.GOLD);
  doc.rect(0, HEADER_H - 2, PAGE_W, 2, 'F');

  // Logo placeholder (left circle with M monogram)
  const logoX = M;
  const logoY = 9;
  const logoR = 17;

  // Outer gold ring
  applyStroke(doc, C.GOLD);
  doc.setLineWidth(1);
  doc.circle(logoX + logoR, logoY + logoR, logoR, 'S');

  // Inner blue fill
  applyFill(doc, [20, 55, 115]);
  doc.circle(logoX + logoR, logoY + logoR, logoR - 1, 'F');

  // "M" monogram text
  applyText(doc, C.WHITE);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('M', logoX + logoR, logoY + logoR + 6, { align: 'center' });

  // Company name and title (right of logo)
  const txtX = logoX + logoR * 2 + 8;

  applyText(doc, C.GOLD);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text('MONEYPLANT FX LTD', txtX, logoY + 9);

  applyText(doc, C.WHITE);
  doc.setFontSize(15);
  doc.setFont('helvetica', 'bold');
  doc.text('CORPORATE ACCOUNT', txtX, logoY + 18);
  doc.text('APPLICATION FORM', txtX, logoY + 27);

  applyText(doc, [180, 200, 230]);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text('Confidential Business Document', txtX, logoY + 35);

  // Date badge (top right)
  const dateStr = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  applyFill(doc, [20, 55, 115]);
  doc.rect(PAGE_W - M - 42, 8, 42, 10, 'F');
  applyText(doc, C.GOLD);
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'bold');
  doc.text('DATE GENERATED', PAGE_W - M - 21, 13.5, { align: 'center' });
  applyText(doc, C.WHITE);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.text(dateStr, PAGE_W - M - 21, 17, { align: 'center' });

  // "For Official Use" badge
  applyFill(doc, [20, 55, 115]);
  doc.rect(PAGE_W - M - 42, 22, 42, 8, 'F');
  applyText(doc, [180, 200, 230]);
  doc.setFontSize(6.5);
  doc.setFont('helvetica', 'italic');
  doc.text('For Official Use Only', PAGE_W - M - 21, 27, { align: 'center' });

  // Reference number line
  applyText(doc, [180, 200, 230]);
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.text('Ref No: ________________________', PAGE_W - M - 46, 38);

  return HEADER_H + 8;
};

// ─────────────────────────────────────────────
// SECTION HEADER  (numbered, full-width card)
// ─────────────────────────────────────────────
const addSectionHeader = (doc, y, title) => {
  y = ensureSpace(doc, y, 16);

  const sh = 10;

  // Light blue background
  applyFill(doc, C.BLUE_LIGHT);
  doc.rect(M, y, INNER_W, sh, 'F');

  // Bold left accent bar (navy)
  applyFill(doc, C.NAVY);
  doc.rect(M, y, 3, sh, 'F');

  // Thin bottom border
  applyStroke(doc, C.BLUE_MID);
  doc.setLineWidth(0.4);
  doc.line(M, y + sh, M + INNER_W, y + sh);

  // Section title text
  applyText(doc, C.NAVY);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text(title, M + 6, y + sh - 2.5);

  return y + sh + 5;
};

// ─────────────────────────────────────────────
// SUBSECTION HEADER  (lightweight with teal bullet)
// ─────────────────────────────────────────────
const addSubsectionHeader = (doc, y, title) => {
  y = ensureSpace(doc, y, 10);

  // Teal bullet square
  applyFill(doc, C.TEAL_ACCENT);
  doc.rect(M, y - 3.5, 2.5, 5, 'F');

  applyText(doc, C.BLUE);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text(title, M + 5, y);

  // Thin rule extending to right margin
  applyStroke(doc, C.GRAY_RULE);
  doc.setLineWidth(0.25);
  const tw = doc.getTextWidth(title);
  doc.line(M + 5 + tw + 3, y - 1, M + INNER_W, y - 1);

  return y + 6;
};

// ─────────────────────────────────────────────
// SINGLE FIELD  (label above, bordered input box)
// ─────────────────────────────────────────────
const drawField = (doc, x, y, w, label, value) => {
  // Label text
  applyText(doc, C.GRAY_MID);
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.text(label, x, y + LABEL_H - 1);

  const boxY = y + LABEL_H;

  // Input box
  applyFill(doc, value ? C.WHITE : C.GRAY_BG);
  applyStroke(doc, C.GRAY_LIGHT);
  doc.setLineWidth(0.3);
  doc.rect(x, boxY, w, FIELD_H, 'FD');

  // Pre-filled value
  if (value !== undefined && value !== null && value !== '') {
    let displayValue = value;
    if (typeof displayValue === 'string') {
      displayValue = displayValue.toUpperCase();
    } else {
      displayValue = String(displayValue);
    }
    applyText(doc, C.GRAY_DARK);
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'normal');
    const fitted = doc.splitTextToSize(displayValue, w - 4);
    doc.text(fitted[0], x + 2.5, boxY + FIELD_H - 2.8);
  }
};

// ─────────────────────────────────────────────
// FULL-WIDTH FIELD
// ─────────────────────────────────────────────
const addFullField = (doc, y, label, value) => {
  y = ensureSpace(doc, y, ROW_H);
  drawField(doc, M, y, INNER_W, label, value);
  return y + ROW_H;
};

// ─────────────────────────────────────────────
// TWO-COLUMN FIELD ROW
// ─────────────────────────────────────────────
const addTwoFields = (doc, y, label1, val1, label2, val2) => {
  y = ensureSpace(doc, y, ROW_H);
  drawField(doc, M,                   y, COL_W, label1, val1);
  drawField(doc, M + COL_W + COL_GAP, y, COL_W, label2, val2);
  return y + ROW_H;
};

// ─────────────────────────────────────────────
// SPACER
// ─────────────────────────────────────────────
const addSpacer = (doc, y, h = 4) => {
  y = ensureSpace(doc, y, h + 2);
  return y + h;
};

// ─────────────────────────────────────────────
// NOTE BOX  (info strip)
// ─────────────────────────────────────────────
const addNoteBox = (doc, y, noteText) => {
  y = ensureSpace(doc, y, 12);
  applyFill(doc, [245, 248, 255]);
  applyStroke(doc, C.BLUE_MID);
  doc.setLineWidth(0.3);
  doc.rect(M, y, INNER_W, 10, 'FD');

  applyFill(doc, C.BLUE);
  doc.rect(M, y, 2, 10, 'F');

  applyText(doc, C.GRAY_MID);
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'italic');
  const lines = doc.splitTextToSize(noteText, INNER_W - 8);
  doc.text(lines[0], M + 5, y + 6.5);
  return y + 13;
};

// ─────────────────────────────────────────────
// CHECKBOX ROW  (Yes / No tick boxes)
// ─────────────────────────────────────────────
const addCheckboxRow = (doc, y, label, value) => {
  y = ensureSpace(doc, y, 10);

  applyText(doc, C.GRAY_MID);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text(label, M + 2, y + 5);

  const isYes = value === true || value === 'Yes' || value === 'yes';
  const isNo  = value === false || value === 'No'  || value === 'no';

  // YES box
  applyFill(doc, isYes ? C.BLUE : C.WHITE);
  applyStroke(doc, C.GRAY_LIGHT);
  doc.rect(M + INNER_W - 28, y + 1, 12, 7, 'FD');
  applyText(doc, isYes ? C.WHITE : C.GRAY_MID);
  doc.setFontSize(7.5);
  doc.text('YES', M + INNER_W - 28 + 6, y + 6, { align: 'center' });

  // NO box
  applyFill(doc, isNo ? [200, 30, 30] : C.WHITE);
  applyStroke(doc, C.GRAY_LIGHT);
  doc.rect(M + INNER_W - 14, y + 1, 12, 7, 'FD');
  applyText(doc, isNo ? C.WHITE : C.GRAY_MID);
  doc.text('NO', M + INNER_W - 14 + 6, y + 6, { align: 'center' });

  return y + 11;
};

// ─────────────────────────────────────────────
// SIGNATURE BLOCK
// ─────────────────────────────────────────────
const addSignatureBlock = (doc, y) => {
  y = ensureSpace(doc, y, 42);

  applyText(doc, C.NAVY);
  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'bold');
  doc.text('AUTHORISED SIGNATURES', M, y);
  y += 5;

  applyText(doc, C.FOOTER_TEXT);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'italic');
  const notice = 'By signing below, the authorised signatory(ies) confirm that all information provided in this form is true, complete, and accurate.';
  const noticeLines = doc.splitTextToSize(notice, INNER_W);
  doc.text(noticeLines, M, y);
  y += noticeLines.length * 4 + 3;

  const sigW = (INNER_W - 10) / 2;

  // Box 1 - Applicant
  applyFill(doc, C.GRAY_BG);
  applyStroke(doc, C.GRAY_LIGHT);
  doc.setLineWidth(0.3);
  doc.rect(M, y, sigW, 22, 'FD');
  applyText(doc, C.GRAY_MID);
  doc.setFontSize(7);
  doc.text('Applicant / Authorised Signatory', M + 2, y + 4);
  applyStroke(doc, C.GRAY_LIGHT);
  doc.line(M + 4, y + 16, M + sigW - 4, y + 16);
  applyText(doc, C.GRAY_MID);
  doc.setFontSize(6.5);
  doc.text('Signature & Date', M + 2, y + 20);

  // Box 2 - Moneyplant rep
  applyFill(doc, C.GRAY_BG);
  applyStroke(doc, C.GRAY_LIGHT);
  doc.rect(M + sigW + 10, y, sigW, 22, 'FD');
  applyText(doc, C.GRAY_MID);
  doc.setFontSize(7);
  doc.text('Moneyplant FX Ltd Representative', M + sigW + 12, y + 4);
  applyStroke(doc, C.GRAY_LIGHT);
  doc.line(M + sigW + 14, y + 16, M + sigW + 10 + sigW - 4, y + 16);
  applyText(doc, C.GRAY_MID);
  doc.setFontSize(6.5);
  doc.text('Signature & Date', M + sigW + 12, y + 20);

  return y + 26;
};

// ─────────────────────────────────────────────
// PAGE-LEVEL FOOTER  (every page)
// ─────────────────────────────────────────────
const addFooter = (doc) => {
  const totalPages = doc.internal.getNumberOfPages();
  const PH = doc.internal.pageSize.getHeight();

  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);

    // Footer strip
    applyFill(doc, C.FOOTER_BG);
    doc.rect(0, PH - 10, PAGE_W, 10, 'F');
    applyStroke(doc, C.GRAY_RULE);
    doc.setLineWidth(0.3);
    doc.line(0, PH - 10, PAGE_W, PH - 10);

    applyText(doc, C.FOOTER_TEXT);
    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'normal');
    doc.text('Moneyplant FX Ltd  |  Confidential  |  Corporate Account Application', M, PH - 3.5);
    doc.text(`Page ${p} of ${totalPages}`, PAGE_W - M, PH - 3.5, { align: 'right' });
  }
};

// ─────────────────────────────────────────────
// SECTION 1 - COMPANY INFORMATION
// ─────────────────────────────────────────────
const addCompanyInformation = (doc, y, data) => {
  const d = data || {};
  y = addSectionHeader(doc, y, '1.   COMPANY INFORMATION');

  y = addSubsectionHeader(doc, y, 'Basic Company Details');
  y = addTwoFields(doc, y, 'Corporate Name', d.corporateName, 'Type of Incorporation', d.typeOfIncorporation);
  y = addTwoFields(doc, y, 'Ownership Type', d.ownershipType, 'Country of Incorporation', d.countryOfIncorporation);
  y = addFullField(doc, y, 'Registered Address', d.companyRegisteredAddress);
  y = addTwoFields(doc, y, 'Incorporation Date', d.incorporationDate, 'Incorporation Number', d.incorporationNo);
  y = addTwoFields(doc, y, 'Number of Shareholders', d.numberOfShareholders, 'Number of Staff', d.numberOfStaff);
  y = addTwoFields(doc, y, 'Mobile', d.mobile, 'Email', d.email);

  y = addSpacer(doc, y, 3);
  y = addSubsectionHeader(doc, y, 'Regulatory Information');
  y = addTwoFields(doc, y, 'Regulatory Authority', d.regulatoryAuthority, 'Activity License No.', d.activityLicenseNo);
  y = addTwoFields(doc, y, 'License Expiry Date', d.licenseExpiry, 'Main Business Activity', d.mainBusinessActivity);

  y = addSpacer(doc, y, 3);
  y = addSubsectionHeader(doc, y, 'Financial Information');
  y = addTwoFields(doc, y, 'Annual Turnover', d.annualTurnover, 'Net Assets', d.netAssets);
  y = addTwoFields(doc, y, 'Liquid Portfolio', d.liquidPortfolio, 'Funds Available', d.fundsAvailable);
  y = addTwoFields(doc, y, 'Source of Wealth', d.sourceOfWealth, 'Corporate Tax Number', d.corporateTaxNumber);

  y = addSpacer(doc, y, 3);
  y = addSubsectionHeader(doc, y, 'Trading Information');
  y = addTwoFields(doc, y, 'Primary Purpose', d.primaryPurpose, 'Prior Trading Experience', d.priorExperience);
  y = addTwoFields(doc, y, 'Leveraged Products Experience', d.leveragedExperience, 'Understands Risks', d.understandRisks);

  y = addSpacer(doc, y, 3);
  y = addSubsectionHeader(doc, y, 'Products Experience');
  y = addNoteBox(doc, y, 'The following indicates products the company has prior trading experience with.');

  const pe = Array.isArray(d.productsExperience) ? d.productsExperience : [];
  const hasSpot = pe.includes('Spot');
  const hasFutures = pe.includes('Futures');
  const hasCFDs = pe.includes('CFDs');
  const hasShares = pe.includes('Shares');
  const hasIndices = pe.includes('Indices');

  y = addCheckboxRow(doc, y, 'Spot Trading', hasSpot ? 'Yes' : undefined);
  y = addCheckboxRow(doc, y, 'Futures Trading', hasFutures ? 'Yes' : undefined);
  y = addCheckboxRow(doc, y, 'CFDs Trading', hasCFDs ? 'Yes' : undefined);
  y = addCheckboxRow(doc, y, 'Shares Trading', hasShares ? 'Yes' : undefined);
  y = addCheckboxRow(doc, y, 'Indices Trading', hasIndices ? 'Yes' : undefined);

  y = addSpacer(doc, y, 3);
  y = addSubsectionHeader(doc, y, 'Compliance Declaration');
  y = addCheckboxRow(doc, y, 'Does the company hold an account with Moneyplant FX?', d.accountWithVPFX);
  y = addCheckboxRow(doc, y, 'Any past complaints or legal issues?', d.pastComplaints);
  y = addCheckboxRow(doc, y, 'Is the company a US entity or US tax resident?', d.usEntity);
  y = addCheckboxRow(doc, y, 'Is the company subject to bankruptcy or insolvency proceedings?', d.bankruptcy);
  y = addCheckboxRow(doc, y, 'Any criminal or regulatory proceedings?', d.criminalProceedings);
  y = addCheckboxRow(doc, y, 'Does the company act as a broker or intermediary?', d.brokerIntermediary);

  y = addSpacer(doc, y, 3);
  y = addSubsectionHeader(doc, y, 'Account Security');
  y = addTwoFields(doc, y, 'Password', d.password ? '••••••••' : '', 'Confirm Password', d.confirmPassword ? '••••••••' : '');

  return y + 8;
};

// ─────────────────────────────────────────────
// SECTION 2 - CORPORATE OFFICERS
// ─────────────────────────────────────────────
const addCorporateOfficers = (doc, y, data) => {
  const officers = (data && data.officers && data.officers.length > 0) ? data.officers : [{}];
  y = addSectionHeader(doc, y, '2.   CORPORATE OFFICERS');

  officers.forEach((officer, idx) => {
    const o = officer || {};
    y = addSubsectionHeader(doc, y, `Officer ${idx + 1}`);
    y = addTwoFields(doc, y, 'Officer Title', o.officerTitle, 'Job Title / Designation', o.jobTitle);
    y = addTwoFields(doc, y, 'Rights', o.rights, 'Holding (%)', o.holding);
    y = addFullField(doc, y, 'Full Name', o.fullName);
    y = addTwoFields(doc, y, 'Gender', o.gender, 'Date of Birth', o.dob);
    y = addTwoFields(doc, y, 'Mobile', o.mobile, 'Email', o.email);
    y = addTwoFields(doc, y, 'Primary Nationality', o.primaryNationality, 'Secondary Nationality', o.secondaryNationality);
    y = addTwoFields(doc, y, 'Country of Residence', o.countryOfResidence, 'Passport Number', o.passportNumber);
    y = addTwoFields(doc, y, 'Passport Expiry Date', o.expiryDate, '', '');
    y = addFullField(doc, y, 'Full Residential Address', o.fullAddress);
    y = addSpacer(doc, y, 5);
  });

  return y + 6;
};

// ─────────────────────────────────────────────
// SECTION 3 - DOCUMENTS VERIFICATION
// ─────────────────────────────────────────────
const addDocumentsSection = (doc, y, data) => {
  const documents = (data && data.documents && data.documents.length > 0) ? data.documents : [{}];
  y = addSectionHeader(doc, y, '3.   DOCUMENTS VERIFICATION');

  documents.forEach((docItem, idx) => {
    const d = docItem || {};
    y = addSubsectionHeader(doc, y, `Document ${idx + 1}`);
    y = addTwoFields(doc, y, 'Document Type', d.documentType, 'Document Number', d.documentNo);
    y = addTwoFields(doc, y, 'Expiry Date', d.expiryDate, 'File Status',
      d.file && (d.file instanceof File || d.file.name || d.file.length > 0) ? 'Uploaded' : 'Not Uploaded');
    y = addSpacer(doc, y, 4);
  });

  return y + 6;
};

// ─────────────────────────────────────────────
// SECTION 4 - BANK ACCOUNTS
// ─────────────────────────────────────────────
const addBankAccounts = (doc, y, data) => {
  const accounts = (data && data.bankAccounts && data.bankAccounts.length > 0) ? data.bankAccounts : [{}];
  y = addSectionHeader(doc, y, '4.   BANK ACCOUNTS');

  accounts.forEach((account, idx) => {
    const a = account || {};
    y = addSubsectionHeader(doc, y, `Bank Account ${idx + 1}`);
    y = addTwoFields(doc, y, 'Bank Name', a.bankName, 'Account Name', a.accountName);
    y = addTwoFields(doc, y, 'Account Number', a.accountNumber, 'Currency', a.currency);
    y = addTwoFields(doc, y, 'SWIFT / BIC Code', a.swiftCode, 'IBAN', a.iban);
    y = addFullField(doc, y, 'Bank Address', a.bankAddress);
    y = addSpacer(doc, y, 5);
  });

  return y + 6;
};

// ─────────────────────────────────────────────
// MAIN GENERATOR
// ─────────────────────────────────────────────
export const generateCorporateSignupPDF = (formData) => {
  const doc = new jsPDF({ unit: 'mm', format: 'a4', compress: true });

  // Light background tint on all pages
  applyFill(doc, [248, 250, 255]);
  doc.rect(0, 0, PAGE_W, 297, 'F');

  let y = addHeader(doc);

  y = addCompanyInformation(doc, y, formData[0]);
  y = addCorporateOfficers(doc, y, formData[1]);
  y = addBankAccounts(doc, y, formData[3]);

  // Signature block
  y = ensureSpace(doc, y, 45);
  y = addSignatureBlock(doc, y);

  // Footer on all pages (must be last call)
  addFooter(doc);

  return doc;
};

// ─────────────────────────────────────────────
// DOWNLOAD TRIGGER
// ─────────────────────────────────────────────
export const downloadCorporateSignupPDF = (formData) => {
  const pdfDoc = generateCorporateSignupPDF(formData);
  const name = formData?.[0]?.corporateName || 'Application';
  const date = new Date().toISOString().split('T')[0];
  pdfDoc.save(`MoneyplantFX_Corporate_${name}_${date}.pdf`);
};